/**
 * Israeli bank branch helpers using BOI branches XML.
 * Uses static file in project (same-origin, no CORS): frontend/snifim_he.xml
 */

const BOI_BRANCHES_XML_URL = '/tqtag/js/form/frontend/snifim_he.xml';

let cachedBranchMap = null;
/** @type {Map<string, Array<{branchCode: string, branchName: string}>>} */
let cachedBranchesByBank = null;
/** @type {Map<string, string>} bank name -> bank code (from XML <id>) */
let cachedBankCodes = null;
let loadPromise = null;

/**
 * Normalize string for lookup (trim).
 * @param {*} v
 * @returns {string}
 */
function normalize(v) {
  if (v == null) return '';
  return String(v).trim();
}

/**
 * Normalize string for map key so that bank names with " (or &quot;) match consistently.
 * Decode HTML entity and collapse quote variants to a single character so lookup works.
 * @param {*} v
 * @returns {string}
 */
function normalizeForKey(v) {
  let s = normalize(v);
  if (s === '') return '';
  // Decode &quot; so keys from XML and form data align
  s = s.replace(/&quot;/gi, '"');
  return s;
}

/**
 * Parse BOI branches XML; returns { branchMap, branchesByBank }.
 * @param {string} xmlText
 * @returns {{ branchMap: Map<string, string>, branchesByBank: Map<string, Array<{branchCode: string, branchName: string}>> }}
 */
function parseBranchesXml(xmlText) {
  const branchMap = new Map();
  const branchesByBank = new Map();
  const bankCodes = new Map();
  if (!xmlText || typeof xmlText !== 'string') return { branchMap, branchesByBank, bankCodes };

  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'text/xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    return { branchMap, branchesByBank, bankCodes };
  }

  const branches = doc.querySelectorAll('branch');
  branches.forEach(branchEl => {
    const nameEl = branchEl.querySelector('name');
    const idEl = branchEl.querySelector('id');
    const codeEl = branchEl.querySelector('branch_code');
    const branchNameEl = branchEl.querySelector('branch_name');
    const bankName = nameEl ? normalizeForKey(nameEl.textContent) : '';
    const bankCode = idEl ? normalize(idEl.textContent) : '';
    const branchCode = codeEl ? normalize(codeEl.textContent) : '';
    const branchName = branchNameEl ? normalize(branchNameEl.textContent) : '';
    if (bankName !== '' && bankCode !== '' && !bankCodes.has(bankName)) bankCodes.set(bankName, bankCode);
    if (bankName !== '' || branchCode !== '') {
      const key = `${bankName}|${branchCode}`;
      branchMap.set(key, branchName || branchCode);
      if (!branchesByBank.has(bankName)) branchesByBank.set(bankName, []);
      branchesByBank.get(bankName).push({ branchCode, branchName: branchName || branchCode });
    }
  });

  return { branchMap, branchesByBank, bankCodes };
}

/**
 * Load branches data from BOI XML (cached after first load).
 * @param {{ url?: string }} options - Optional. url: same-origin proxy URL if CORS blocks default.
 * @returns {Promise<Map<string, string>>}
 */
function loadBranchMapInternal(options = {}) {
  if (cachedBranchMap) return Promise.resolve(cachedBranchMap);
  if (loadPromise) return loadPromise;

  const url = (options && options.url) || BOI_BRANCHES_XML_URL;

  loadPromise = fetch(url, { credentials: 'same-origin' })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    })
    .then(xmlText => {
      const { branchMap, branchesByBank: byBank, bankCodes } = parseBranchesXml(xmlText);
      cachedBranchMap = branchMap;
      cachedBranchesByBank = byBank;
      cachedBankCodes = bankCodes;
      return cachedBranchMap;
    })
    .catch(err => {
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}

/**
 * Load branches data from BOI XML (cached after first load). Exported for callers that need sync lookups after await.
 * @param {{ url?: string }} options - Optional. url: same-origin URL for XML (e.g. proxy) if CORS blocks.
 * @returns {Promise<Map<string, string>>}
 */
export function loadBranchMap(options = {}) {
  return loadBranchMapInternal(options);
}

/**
 * Get bank names from XML (for populating bank dropdown). Must call loadBranchMap() first.
 * @param {{ url?: string }} options - Load options
 * @param {{ excludeBankCodes?: string[] }} filterOpts - If provided, exclude banks whose code is in this array
 * @returns {Promise<string[]>} Sorted list of bank names
 */
export function getBankNamesFromXml(options = {}, filterOpts = {}) {
  return loadBranchMapInternal(options).then(() => {
    if (!cachedBranchesByBank) return [];
    let names = Array.from(cachedBranchesByBank.keys()).sort((a, b) => String(a).localeCompare(String(b)));
    const excludeCodes = filterOpts.excludeBankCodes;
    if (Array.isArray(excludeCodes) && excludeCodes.length > 0 && cachedBankCodes) {
      const excludeSet = new Set(excludeCodes.map(c => String(c).trim()).filter(Boolean));
      names = names.filter(name => {
        const code = cachedBankCodes.get(name);
        return code == null || !excludeSet.has(String(code));
      });
    }
    return names;
  });
}

/**
 * Get list of branches for a bank (for dropdown/datalist). Must call loadBranchMap() first.
 * Bank name must match XML exactly (populate bank dropdown from getBankNamesFromXml()).
 * @param {string} bankName
 * @param {{ url?: string }} options - Load options
 * @param {{ excludeBranchCodes?: string[] }} filterOpts - If provided, exclude branches whose branchCode is in this array
 * @returns {Promise<Array<{branchCode: string, branchName: string}>>}
 */
export function getBranchesForBank(bankName, options = {}, filterOpts = {}) {
  const b = normalizeForKey(bankName);
  if (b === '') return Promise.resolve([]);
  return loadBranchMapInternal(options).then(() => {
    if (!cachedBranchesByBank) return [];
    let list = cachedBranchesByBank.get(b);
    list = Array.isArray(list) ? list : [];
    const excludeCodes = filterOpts.excludeBranchCodes;
    if (Array.isArray(excludeCodes) && excludeCodes.length > 0) {
      const excludeSet = new Set(excludeCodes.map(c => String(c).trim()).filter(Boolean));
      list = list.filter(item => !excludeSet.has(String(item.branchCode || '').trim()));
    }
    return list;
  });
}

/**
 * Resolve branch name from bank name and branch code using BOI data (callback style).
 * @param {string} bankName - Bank name (as in BOI XML <name>)
 * @param {string} branchCode - Branch code (as in BOI XML <branch_code>)
 * @param {function(string): void} callback - Called with branch name, or branchCode if not found
 */
export function getBankBranchNameFromCode(bankName, branchCode, callback) {
  if (typeof callback !== 'function') return;

  const b = normalizeForKey(bankName);
  const c = normalize(branchCode);
  if (c === '') {
    callback('');
    return;
  }

  loadBranchMapInternal({}).then(map => {
    const key = `${b}|${c}`;
    const branchName = map.get(key);
    callback(branchName !== undefined ? branchName : c);
  });
}

/**
 * Get bank code for a bank name (from BOI XML <id>). Must call loadBranchMap() first.
 * @param {string} bankName - Bank name as in XML
 * @param {{ url?: string }} options - Optional
 * @returns {Promise<string>} Bank code or empty string if not found
 */
export function getBankCodeForBankName(bankName, options = {}) {
  const b = normalizeForKey(bankName);
  if (b === '') return Promise.resolve('');
  return loadBranchMapInternal(options).then(() => {
    if (!cachedBankCodes) return '';
    return cachedBankCodes.get(b) || '';
  });
}

/**
 * Synchronous lookup once the map is loaded. Key format: "bankName|branchCode".
 * @param {Map<string, string>} map - From loadBranchMap()
 * @param {string} bankName
 * @param {string} branchCode
 * @returns {string} Branch name or branchCode if not found
 */
export function getBranchNameFromMap(map, bankName, branchCode) {
  if (!map || typeof map.get !== 'function') return normalize(branchCode);
  const b = normalizeForKey(bankName);
  const c = normalize(branchCode);
  if (c === '') return '';
  const key = `${b}|${c}`;
  const name = map.get(key);
  return name !== undefined ? name : c;
}
